import * as Checkbox from '@radix-ui/react-checkbox';
import { combineRefs } from '@visiba/react-std';
import { ForwardedRef, forwardRef, useEffect, useState } from 'react';
import { Controller, RegisterOptions } from 'react-hook-form';
import styled from 'styled-components';
import { coreTokens } from '../../../core';
import { Icon } from '../../icon';
import { theme as commonTheme } from '../common_input.theme';
import { useFormContext } from '../form';
import { InputPropsHelper } from '../form/form.helpers';
import { useInputCheckboxGroupContext } from './_input_checkbox_group.context';
import { theme } from './input_checkbox.theme';

const elements = {
	item: styled(Checkbox.Root)`
		--background: none;
		--indicator-border-color: ${theme.getValue('indicatorBorderColor')};
		--indicator-background: ${theme.getValue('indicatorBackground')};
		--indicator-outline: none;

		all: unset;

		user-select: none;
		-webkit-tap-highlight-color: transparent;
		-webkit-user-drag: none;
		-webkit-touch-callout: none;

		box-sizing: border-box;
		display: flex;
		gap: ${theme.getValue('itemGap')};
		align-items: center;
		cursor: default;
		font: ${theme.getValue('itemFont')};
		color: ${theme.getValue('itemTextColor')};
		background: var(--background);
		border-radius: ${coreTokens.borderRadius.xSmall};
		transition:
			border 150ms ease-out,
			background 150ms ease-out,
			box-shadow 50ms ease-out;

		&[data-state='checked'] {
			font: ${theme.getValue('checkedItemFont')};

			--indicator-border-color: ${theme.getValue('checkedIndicatorBorderColor')};
			--indicator-background: ${theme.getValue('checkedIndicatorBackground')};
		}

		&:focus-visible {
			outline: 2px solid ${theme.getValue('focusColor')};
			outline-offset: 2px;
		}

		&[data-variant='none'] {
			&:hover {
				--indicator-border-color: ${theme.getValue('checkedIndicatorBorderColor')};
			}

			&:active {
				--indicator-outline: 0 0 0 2px ${theme.getValue('activeIndicatorOutlineColor')};
			}

			&[data-invalid='true'] {
				--indicator-border-color: ${commonTheme.getValue('invalidBorderColor')};
			}
		}

		&[data-variant='outlined'] {
			padding: ${theme.getValue('outlinedItemPadding')};
			border: 1px solid ${theme.getValue('outlinedItemBorderColor')};
			min-height: ${theme.getValue('outlinedItemMinHeight')};
			border-radius: ${theme.getValue('outlinedItemBorderRadius')};

			&:hover {
				border-color: ${theme.getValue('hoverOutlinedItemBorderColor')};
			}

			&:active {
				box-shadow: 0 0 0 2px ${theme.getValue('activeOutlinedItemOutlineColor')};
			}

			&[data-state='checked'] {
				--background: ${theme.getValue('checkedOutlinedItemBackground')};
			}

			&[data-invalid='true'] {
				border-color: ${commonTheme.getValue('invalidBorderColor')};
			}
		}

		// Disabled/ReadOnly
		&[data-read-only='true'],
		&[data-disabled='true'] {
			--indicator-border-color: ${theme.getValue('disabledIndicatorBorderColor')};
			--indicator-background: ${theme.getValue('disabledIndicatorBackground')};
			opacity: 0.7;

			&:hover,
			&:active {
				--indicator-border-color: ${theme.getValue('disabledIndicatorBorderColor')};
				--indicator-outline: none;
			}

			&[data-variant='outlined'] {
				--background: ${theme.getValue('disabledOutlinedItemBackground')};
				border: none;

				&:hover,
				&:active {
					box-shadow: none;
				}
			}

			&[data-state='checked'] {
				--indicator-background: ${theme.getValue('disabledIndicatorBorderColor')};
			}
		}
	`,
	check: styled.span`
		position: relative;
		display: flex;
		width: ${theme.getValue('indicatorSize')};
		height: ${theme.getValue('indicatorSize')};
		border: 2px solid var(--indicator-border-color);
		border-radius: 4px;
		align-items: center;
		justify-content: center;
		box-shadow: var(--indicator-outline);
		background: var(--indicator-background);
		transition:
			border 150ms ease-out,
			background 100ms ease-out,
			box-shadow 50ms ease-out;
		flex: 0 0 auto;

		svg {
			width: 100%;
			height: 100%;
		}
	`,
	indicator: styled(Checkbox.Indicator)`
		display: block;
		color: #fff;
		margin: -2px;
	`,
	content: styled.span`
		display: flex;
		flex-direction: column;
		gap: 0;
	`,
	label: styled.span`
		display: inline-flex;
		flex-direction: column;
	`,
	subLabel: styled.span`
		display: inline-flex;
		flex-direction: column;
		font: ${theme.getValue('subLabelFont')};
	`,
};
type InputCheckboxVariants = 'none' | 'outlined';

interface InputCheckboxProps {
	label: string;
	hideLabel?: boolean;
	subLabel?: string;
	variant?: InputCheckboxVariants;
	name?: string;
	readOnly?: boolean;
	disabled?: boolean;
	invalid?: boolean;
	onChange?: (value: boolean) => void;
	checked?: boolean;
	value?: string;
	defaultChecked?: boolean;
	options?: RegisterOptions;
	required?: boolean;
}

const InputCheckboxRoot = forwardRef<HTMLButtonElement, InputCheckboxProps>(function InputCheckboxRoot(
	{
		label,
		hideLabel = false,
		subLabel,
		variant = 'none',
		invalid,
		readOnly,
		disabled,
		onChange,
		checked,
		value,
		required,
		...rest
	}: InputCheckboxProps,
	ref: ForwardedRef<HTMLButtonElement>,
): JSX.Element {
	// We need to use a state for this to make it possible to support read only state.
	const [isCheck, setIsChecked] = useState(checked ?? false);

	useEffect(
		function whenCheckedChanges() {
			setIsChecked(checked ?? false);
		},
		[checked],
	);

	function handleValueChange(checked: boolean): void {
		if (readOnly) {
			return;
		}

		setIsChecked(checked);
		onChange?.(checked);
	}

	return (
		<elements.item
			onCheckedChange={handleValueChange}
			disabled={disabled}
			data-variant={variant}
			data-invalid={invalid ?? undefined}
			aria-readonly={readOnly ?? undefined}
			data-read-only={readOnly ?? undefined}
			data-disabled={disabled ?? undefined}
			aria-label={hideLabel ? label : undefined}
			checked={isCheck}
			value={value}
			required={required}
			ref={ref}
			{...rest}
		>
			<elements.check>
				<elements.indicator>
					<Icon type='check' />
				</elements.indicator>
			</elements.check>
			{!hideLabel ? (
				<elements.content>
					{/** biome-ignore lint/a11y/noLabelWithoutControl: suppressed due to migration - should be fixed */}
					<elements.label>{label}</elements.label>
					{subLabel ? <elements.subLabel>{subLabel}</elements.subLabel> : null}
				</elements.content>
			) : null}
		</elements.item>
	);
});

export function CellulaInputCheckbox(props: InputCheckboxProps, ref: ForwardedRef<HTMLButtonElement>): JSX.Element {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const formContext = useFormContext();
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const inputCheckboxGroupContext = useInputCheckboxGroupContext();

	if (inputCheckboxGroupContext) {
		props = {
			...props,
			invalid: props.invalid ?? inputCheckboxGroupContext.invalid,
			disabled: props.disabled ?? inputCheckboxGroupContext.disabled,
			readOnly: props.readOnly ?? inputCheckboxGroupContext.readOnly,
			variant: props.variant ?? inputCheckboxGroupContext.variant,
		};

		if (inputCheckboxGroupContext.name) {
			return (
				<InputCheckboxRoot
					{...{
						...props,
						name: undefined,
					}}
				/>
			);
		}
	}

	if (formContext == null || props.name == null) {
		return <InputCheckboxRoot {...props} />;
	}

	function handleChange(event: { target: { value: boolean } }): void {
		props.onChange?.(typeof event.target.value === 'boolean' ? event.target.value : false);
	}

	return (
		<Controller
			control={formContext.control.control}
			name={props.name}
			defaultValue={props.defaultChecked ?? false}
			rules={{
				...props.options,
				onChange: handleChange,
			}}
			disabled={props.disabled}
			render={({ field, fieldState, formState }) => {
				const inputPropsHelper = new InputPropsHelper(props, null, fieldState, formState);

				return (
					<InputCheckboxRoot
						{...props}
						{...field}
						required={inputPropsHelper.isRequired()}
						readOnly={inputPropsHelper.isReadOnly()}
						invalid={inputPropsHelper.isInvalid()}
						checked={!!field.value}
						ref={combineRefs([ref, field.ref])}
					/>
				);
			}}
		/>
	);
}

export const InputCheckbox = forwardRef(CellulaInputCheckbox);
export type { InputCheckboxProps, InputCheckboxVariants };
